// src/pages/admin/Sizes.jsx - MOBILE RESPONSIVE FIXED
import React, { useState, useEffect } from 'react';
import { useOptions } from '../../context/OptionsContext';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import { Add, Edit, Delete, Check, Close, Refresh, MoreVert } from '@mui/icons-material';

const Sizes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { sizes, loading, fetchAllOptions } = useOptions();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [formData, setFormData] = useState({ name: '', displayOrder: 0 });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  const handleMenuOpen = (event, size) => {
    setAnchorEl(event.currentTarget);
    setSelectedSize(size);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (size = null) => {
    if (size) {
      setEditingSize(size);
      setFormData({ name: size.name, displayOrder: size.displayOrder || 0 });
    } else {
      setEditingSize(null);
      setFormData({ name: '', displayOrder: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSize(null);
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (editingSize) {
        await adminApi.updateSize(editingSize.id, formData);
        setSuccess('Size updated successfully!');
      } else {
        await adminApi.createSize(formData);
        setSuccess('Size created successfully!');
      }
      handleCloseDialog();
      fetchAllOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save size');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this size?')) {
      try {
        await adminApi.deleteSize(id);
        setSuccess('Size deleted successfully!');
        fetchAllOptions();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete size');
      }
    }
  };

  const handleToggleStatus = async (size) => {
    try {
      const updatedStatus = !size.isActive;
      await adminApi.updateSize(size.id, { isActive: updatedStatus });
      setSuccess(`Size "${size.name}" ${updatedStatus ? 'activated' : 'deactivated'}`);
      fetchAllOptions();
      handleMenuClose();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle size status');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Sizes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage product size options ({sizes.length} total)
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={fetchAllOptions}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => handleOpenDialog()}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ borderRadius: 2 }}
            >
              Add Size
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Mobile Card View */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sizes.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">No sizes found</Typography>
            </Paper>
          ) : (
            sizes.map((size) => (
              <Card key={size.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {size.name}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, size)}>
                        <MoreVert />
                      </IconButton>
                    </Stack>
                    
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Order: {size.displayOrder || 0}
                      </Typography>
                      <Chip
                        icon={size.isActive ? <Check /> : <Close />}
                        label={size.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={size.isActive ? 'success' : 'error'}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Name</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Display Order</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sizes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No sizes found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sizes.map((size) => (
                    <TableRow key={size.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">{size.name}</Typography>
                      </TableCell>
                      <TableCell>{size.displayOrder || 0}</TableCell>
                      <TableCell>
                        <Chip
                          icon={size.isActive ? <Check /> : <Close />}
                          label={size.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={size.isActive ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, size)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleOpenDialog(selectedSize); handleMenuClose(); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(selectedSize)}>
          {selectedSize?.isActive ? (
            <>
              <Close fontSize="small" sx={{ mr: 1 }} /> Deactivate
            </>
          ) : (
            <>
              <Check fontSize="small" sx={{ mr: 1 }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedSize?.id)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {editingSize ? 'Edit Size' : 'Add New Size'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Size Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={handleCloseDialog} fullWidth={isMobile}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} fullWidth={isMobile}>
            {editingSize ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sizes;